/**
 * useRhymeSearch Hook
 *
 * Manages rhyme search queries with client-side caching and loading floor.
 * Caches responses keyed by word + stability to minimize API calls.
 * Enforces 300ms minimum loading state for consistent UX.
 */

import { useState, useCallback, useRef } from 'react'
import { backendApiClient } from '../lib/backendApi'
import type { RhymeSearchResponse } from '../types/wordTools'

// Cache TTL - responses older than this trigger background refresh
const CACHE_TTL_MS = 20000 // 20 seconds

// Minimum loading display time for consistent skeleton visibility
const MIN_LOADING_MS = 300

interface CacheEntry {
  data: RhymeSearchResponse
  timestamp: number
}

interface UseRhymeSearchResult {
  /** Current search results (may be cached) */
  results: RhymeSearchResponse | null
  /** Is a search currently in progress? */
  isLoading: boolean
  /** Error from most recent search */
  error: string | null
  /** Is current result from cache? */
  isCached: boolean
  /** Is cached result stale (background refresh in progress)? */
  isStale: boolean
  /** Execute a rhyme search query */
  search: (word: string, stability?: string) => Promise<void>
  /** Clear current results and cache for specific query */
  clearResults: () => void
  /** Clear entire cache (used when worksheet closes) */
  clearCache: () => void
}

/**
 * Hook for managing rhyme search with caching and loading floor.
 *
 * Features:
 * - Client-side cache keyed by word + stability
 * - 300ms minimum loading state (prevents flash on fast responses)
 * - Background refresh for stale cached entries
 * - Exposes cache state for UI indicators
 *
 * @example
 * ```typescript
 * const { results, isLoading, error, search } = useRhymeSearch()
 *
 * // Search with stability context
 * await search('love', 'stable')
 *
 * // Results available with metadata
 * if (results) {
 *   console.log(results.categories.perfect)
 * }
 * ```
 */
export function useRhymeSearch(): UseRhymeSearchResult {
  // Cache storage - persists across re-renders
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map())

  // Current search state
  const [results, setResults] = useState<RhymeSearchResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isCached, setIsCached] = useState(false)
  const [isStale, setIsStale] = useState(false)

  // Track active request to prevent race conditions
  const activeRequestRef = useRef<string | null>(null)

  /**
   * Generate cache key from word + stability.
   * Normalized to lowercase for consistency.
   */
  const getCacheKey = useCallback((word: string, stability?: string): string => {
    const normalizedWord = word.toLowerCase().trim()
    const normalizedStability = stability || 'unknown'
    return `${normalizedWord}:${normalizedStability}`
  }, [])

  /**
   * Check if cache entry is stale (older than TTL).
   */
  const isEntrystale = useCallback((entry: CacheEntry): boolean => {
    return Date.now() - entry.timestamp > CACHE_TTL_MS
  }, [])

  /**
   * Execute rhyme search with caching and loading floor.
   */
  const search = useCallback(async (word: string, stability?: string): Promise<void> => {
    console.log('[useRhymeSearch] search called:', { word, stability })

    if (!word.trim()) {
      setResults(null)
      setError(null)
      setIsCached(false)
      setIsStale(false)
      return
    }

    const cacheKey = getCacheKey(word, stability)
    const cachedEntry = cacheRef.current.get(cacheKey)

    console.log('[useRhymeSearch] Cache status:', {
      cacheKey,
      hasCachedEntry: !!cachedEntry,
      cachedTimestamp: cachedEntry?.timestamp,
      ageMs: cachedEntry ? Date.now() - cachedEntry.timestamp : null
    })

    // If we have cached data, show it immediately
    if (cachedEntry) {
      setResults(cachedEntry.data)
      setIsCached(true)
      setError(null)

      const entryIsStale = isEntrystale(cachedEntry)
      setIsStale(entryIsStale)

      // If fresh enough, skip the API call
      if (!entryIsStale) {
        console.log('[useRhymeSearch] Using fresh cache, skipping API call')
        setIsLoading(false)
        return
      }

      console.log('[useRhymeSearch] Cache stale, triggering background refresh')
      // Stale data - trigger background refresh but don't show loading
      // User sees cached data while fresh data loads
    } else {
      console.log('[useRhymeSearch] No cache, fetching from API')
      // No cache - show loading state
      setIsLoading(true)
      setIsCached(false)
      setIsStale(false)
    }

    // Track this as the active request
    activeRequestRef.current = cacheKey

    try {
      // Start loading timer and API call simultaneously
      const loadingStart = Date.now()
      const apiPromise = backendApiClient.searchRhymes(word, stability)

      // Wait for both API response and minimum loading time
      const [response] = await Promise.all([
        apiPromise,
        new Promise(resolve => {
          const elapsed = Date.now() - loadingStart
          const remaining = Math.max(0, MIN_LOADING_MS - elapsed)
          setTimeout(resolve, remaining)
        })
      ])

      // Ignore stale responses if user triggered new search
      if (activeRequestRef.current !== cacheKey) {
        console.log('[useRhymeSearch] Ignoring stale response for:', cacheKey)
        return
      }

      console.log('[useRhymeSearch] API response received:', {
        word: response.word,
        perfectCount: response.categories?.perfect?.syllables_1?.length || 0,
        cached: response.cached,
        processingTime: response.processing_time_ms
      })

      // Update cache
      cacheRef.current.set(cacheKey, {
        data: response,
        timestamp: Date.now()
      })

      // Update state
      setResults(response)
      setError(null)
      setIsCached(false)
      setIsStale(false)
      setIsLoading(false)

    } catch (err) {
      // Ignore errors from stale requests
      if (activeRequestRef.current !== cacheKey) {
        return
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to search rhymes'
      setError(errorMessage)
      setIsLoading(false)
      setIsStale(false)

      // Keep cached data visible on error if available
      if (!cachedEntry) {
        setResults(null)
        setIsCached(false)
      }
    }
  }, [getCacheKey, isEntrystale])

  /**
   * Clear current results (but keep cache intact).
   */
  const clearResults = useCallback(() => {
    setResults(null)
    setError(null)
    setIsLoading(false)
    setIsCached(false)
    setIsStale(false)
    activeRequestRef.current = null
  }, [])

  /**
   * Clear entire cache (use when worksheet closes).
   */
  const clearCache = useCallback(() => {
    cacheRef.current.clear()
    clearResults()
  }, [clearResults])

  return {
    results,
    isLoading,
    error,
    isCached,
    isStale,
    search,
    clearResults,
    clearCache
  }
}
